import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@heroicons/react/20/solid';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Text } from '../../../components/Text';
import { AiReportDatasetColumn, DATASET_MAX_COLUMNS, DATASET_MIN_COLUMNS } from '../../../models/AiReportDataset';

export function newColumnKey(): string {
    return `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

interface ColumnBuilderProps {
    columns: AiReportDatasetColumn[];
    referredKey: string;
    onColumnsChange: (columns: AiReportDatasetColumn[]) => void;
    onReferredKeyChange: (key: string) => void;
}

const ColumnBuilder: React.FC<ColumnBuilderProps> = ({ columns, referredKey, onColumnsChange, onReferredKeyChange }) => {
    const updateLabel = (idx: number, label: string) => {
        const next = columns.map((c, i) => (i === idx ? { ...c, label } : c));
        onColumnsChange(next);
    };

    const moveColumn = (idx: number, direction: -1 | 1) => {
        const target = idx + direction;
        if (target < 0 || target >= columns.length) return;
        const next = [...columns];
        [next[idx], next[target]] = [next[target], next[idx]];
        onColumnsChange(next);
    };

    const removeColumn = (idx: number) => {
        if (columns.length <= DATASET_MIN_COLUMNS) return;
        const removed = columns[idx];
        const next = columns.filter((_, i) => i !== idx);
        onColumnsChange(next);
        if (removed.key === referredKey && next.length > 0) {
            onReferredKeyChange(next[0].key);
        }
    };

    const addColumn = () => {
        if (columns.length >= DATASET_MAX_COLUMNS) return;
        onColumnsChange([...columns, { key: newColumnKey(), label: '' }]);
    };

    return (
        <div className="space-y-3">
            {columns.map((col, idx) => (
                <div key={col.key} className="flex items-center gap-3">
                    <input
                        type="radio"
                        name="referred-column"
                        aria-label={`Mark "${col.label || 'this column'}" as the referred column`}
                        checked={col.key === referredKey}
                        onChange={() => onReferredKeyChange(col.key)}
                        className="size-4 shrink-0"
                    />
                    <Input
                        aria-label={`Column ${idx + 1} label`}
                        placeholder={`Column ${idx + 1} name (e.g. Customer Address)`}
                        value={col.label}
                        onChange={(e) => updateLabel(idx, e.target.value)}
                        className="flex-1"
                    />
                    <button
                        type="button"
                        onClick={() => moveColumn(idx, -1)}
                        disabled={idx === 0}
                        className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30 dark:hover:text-white"
                        aria-label="Move up"
                    >
                        <ArrowUpIcon className="size-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => moveColumn(idx, 1)}
                        disabled={idx === columns.length - 1}
                        className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30 dark:hover:text-white"
                        aria-label="Move down"
                    >
                        <ArrowDownIcon className="size-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => removeColumn(idx)}
                        disabled={columns.length <= DATASET_MIN_COLUMNS}
                        className="text-zinc-400 hover:text-red-600 disabled:opacity-30"
                        aria-label="Remove column"
                    >
                        <TrashIcon className="size-4" />
                    </button>
                </div>
            ))}
            <div className="flex items-center justify-between pt-1">
                <Text className="text-xs text-zinc-500">
                    Select the radio next to the column that uniquely identifies a row (e.g. Customer Name) &mdash;
                    that's the "referred column" reports will match against. {columns.length}/{DATASET_MAX_COLUMNS} columns.
                </Text>
                <Button outline type="button" onClick={addColumn} disabled={columns.length >= DATASET_MAX_COLUMNS}>
                    + Add column
                </Button>
            </div>
        </div>
    );
};

export default ColumnBuilder;
